"use client";

/**
 * HubTabs — a sticky tab strip injected by a hub's layout, so several related
 * pages share one umbrella sidebar slot (the Broadcast pattern, generalised).
 *
 * The tab URLs stay alive verbatim (route groups don't change URLs), so every
 * bookmark / inbound link / share keeps working — only the sidebar slot count
 * drops. Reused by /money, /ai, /community, /events, /studio …
 */

import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { ShareButton } from "@/components/ui/share-button";

export interface HubTab {
  href: string;
  label: string;
  icon: LucideIcon;
}

/** `share` adds a Share button for the page you're on — to someone on BB or
 *  anywhere (Gordon 2026-11-02: every Studio page should be shareable). */
export function HubTabs({ tabs, share }: { tabs: HubTab[]; share?: boolean }) {
  const pathname = usePathname() || "";
  const current = tabs.find(t => pathname === t.href || pathname.startsWith(t.href + "/"));

  return (
    <div className="border-b border-border/50 bg-background/95 backdrop-blur sticky top-[var(--header-height)] z-30">
      <div className="w-full px-page">
        <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar h-12">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = pathname === tab.href || pathname.startsWith(tab.href + "/");
            return (
              <Link
                key={tab.href}
                href={tab.href}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 h-9 rounded-md text-sm font-medium transition-colors flex-shrink-0",
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50",
                )}
                aria-current={active ? "page" : undefined}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </Link>
            );
          })}
          {share && (
            <div className="ml-auto flex-shrink-0 pl-2">
              <ShareButton title={current ? `${current.label} — Breakin’ Bread` : "Breakin’ Bread"} url={pathname}
                iconOnly size="icon" className="h-9 w-9 border-btn-outline-border" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
